<template>
  <nav
    v-if="breadcrumbs.length || status"
    aria-label="Breadcrumb"
    class="mx-auto flex w-full items-center gap-2.5 px-4 md:px-7"
    style="max-width:var(--page-max); min-height:38px; padding-top:2px; padding-bottom:2px; background:var(--bg);"
  >
    <template v-for="(crumb, i) in breadcrumbs" :key="i">
      <!-- the workspace part is the switcher: the one place the current client is chosen -->
      <USelect
        v-if="crumb.workspace && orgOptions.length"
        :model-value="selectedOrgAlias ?? undefined"
        :items="orgOptions"
        size="sm"
        aria-label="Workspace"
        class="min-w-[150px] shrink-0"
        :ui="{ base: 'rounded-full font-semibold' }"
        @update:model-value="switchWorkspace"
      />
      <NuxtLink
        v-else-if="crumb.to && i < breadcrumbs.length - 1"
        :to="crumb.to"
        class="mono crumb-link shrink-0"
        :style="{ fontSize: '13px', color: 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap' }"
      >{{ truncate(crumb.label) }}</NuxtLink>
      <span
        v-else
        :aria-current="i === breadcrumbs.length - 1 ? 'page' : undefined"
        class="mono truncate"
        :style="{
          fontSize: '13px',
          color: i === breadcrumbs.length - 1 ? 'var(--ink)' : 'var(--muted)',
          cursor: 'default',
          minWidth: 0,
        }"
      >{{ truncate(crumb.label) }}</span>
      <span v-if="i < breadcrumbs.length - 1" style="color:var(--faint); font-size:13px;">/</span>
    </template>

    <div class="flex-1" />

    <span
      v-if="status"
      class="mono hidden shrink-0 items-center gap-1.5 md:inline-flex"
      :style="{ fontSize: '13px', color: `var(--${status.color || 'muted'})` }"
    >
      <ConsoleStatusDot :color="status.color || 'muted'" :size="6" />
      {{ status.text }}
    </span>
  </nav>
</template>

<script setup lang="ts">
const breadcrumbs = useBreadcrumbs()
const status = useSubbarStatus()

const route = useRoute()
const userStore = useUserStore()
const { selectedOrgAlias } = storeToRefs(useOrganizationsStore())

const orgOptions = computed(() =>
  (userStore.accessibleOrganizations || []).map((org) => ({ label: org.name, value: org.alias })),
)

function switchWorkspace(alias: string) {
  if (!alias || alias === selectedOrgAlias.value) return
  const leaving = selectedOrgAlias.value
  selectedOrgAlias.value = alias
  // A page for one email, or for a campaign, belongs to the workspace being left.
  // Go to the new workspace's list instead of staying on something it does not own.
  // The overview follows the switcher by itself, and the lists just re-filter.
  if (leaving && (route.path.startsWith(`/app/emails/${leaving}`) || route.path.startsWith('/app/analytics/'))) {
    navigateTo('/app/emails')
  }
}

function truncate(text: string, max = 40): string {
  if (!text) return ''
  return text.length > max ? text.slice(0, max) + '…' : text
}
</script>

<style scoped>
.crumb-link {
  transition: color 0.12s ease;
}
.crumb-link:hover {
  color: var(--ink) !important;
  text-decoration: underline;
  text-underline-offset: 2px;
}
</style>
